import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Row, Col, Tab, Nav, Form, Modal, ListGroup, Table, Alert } from 'react-bootstrap';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import EmailDocumentModal from './EmailDocumentModal';
import EmailPreviewModal from '../common/EmailPreviewModal';
import ScopeModal from './ScopeModal';
import DocumentTypeTag from './DocumentTypeTag';
import MaterialsTab from '../materials/MaterialsTab';
import DocumentsTab from '../documents/DocumentsTab';
import { downloadDocument, downloadFileByName } from '../../utils/documentUtils';
import { FaUserPlus, FaClock, FaList, FaFileInvoiceDollar, FaEnvelope, FaDownload, FaTrash, FaExchangeAlt, FaFileAlt } from 'react-icons/fa';
import ChangeOrderModal from '../changeOrders/ChangeOrderModal';
import ChangeOrderList from '../changeOrders/ChangeOrderList';
import InvoiceModal from '../invoices/InvoiceModal';

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useContext(AuthContext);
  
  const [project, setProject] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [laborSummary, setLaborSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'details');
  
  // State for editing project
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    projectName: '',
    status: '',
    startDate: '',
    endDate: '',
    sumOfTimeAndMaterials: '',
    generalRequirement: ''
  });
  
  // Calculate total cost for editing form
  const calculateEditTotalCost = () => {
    const sum = parseFloat((editFormData.sumOfTimeAndMaterials || '0').replace(/,/g, '') || 0);
    const general = parseFloat((editFormData.generalRequirement || '0').replace(/,/g, '') || 0);
    return sum + general;
  };
  
  // State for cost items
  const [costItems, setCostItems] = useState([]);
  const [showAddCostItem, setShowAddCostItem] = useState(false);
  const [editingCostItemId, setEditingCostItemId] = useState(null);
  const [costItemForm, setCostItemForm] = useState({ description: '', amount: '' });
  
  // State for adding scope
  const [showScopeModal, setShowScopeModal] = useState(false);
  const [scopeFormData, setScopeFormData] = useState({
    scopeName: '',
    scopeInformation: '• ' // Initialize with bullet point
  });
  
  // State for editing scope
  const [isEditingScope, setIsEditingScope] = useState(false);
  const [selectedScopeId, setSelectedScopeId] = useState(null);
  
  // State for generating combined proposal
  const [isGeneratingCombinedProposal, setIsGeneratingCombinedProposal] = useState(false);
  
  // State for document upload
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // State for email modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  
  // State for change orders
  const [showChangeOrderModal, setShowChangeOrderModal] = useState(false);
  const [changeOrders, setChangeOrders] = useState([]);
  
  // State for invoices
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  
  // State for generating proposal (legacy)
  const [isGeneratingProposal, setIsGeneratingProposal] = useState(false);
  
  // State for email preview modal
  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);
  const [emailPreviewData, setEmailPreviewData] = useState(null);
  const [emailPreviewType, setEmailPreviewType] = useState(null);
  const [emailDocumentId, setEmailDocumentId] = useState(null);
  const [emailSigningUrl, setEmailSigningUrl] = useState(null);
  
  // Fetch change orders
  const fetchChangeOrders = async (projectId) => {
    try {
      const response = await fetch(`/api/change-orders/project/${projectId}`, {
        headers: { 'x-auth-token': token }
      });
      if (response.ok) {
        const data = await response.json();
        setChangeOrders(data);
      }
    } catch (err) {
      console.error('Error fetching change orders:', err);
    }
  };

  // Fetch project data
  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await fetch(`/api/projects/${id}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch project details');
        }
        
        const data = await response.json();
        setProject(data);
        
        // Initialize edit form data
        setEditFormData({
          projectName: data.ProjectName,
          status: data.Status,
          startDate: data.StartDate ? new Date(data.StartDate).toISOString().split('T')[0] : '',
          endDate: data.EndDate ? new Date(data.EndDate).toISOString().split('T')[0] : '',
          sumOfTimeAndMaterials: data.SumOfTimeAndMaterials ? data.SumOfTimeAndMaterials.toString() : '',
          generalRequirement: data.GeneralRequirement ? data.GeneralRequirement.toString() : ''
        });
        
        // Set cost items if available
        if (data.costItems) {
          setCostItems(data.costItems);
        }
        
        // Fetch employees assigned to this project
        const employeesResponse = await fetch(`/api/employees/project/${id}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (employeesResponse.ok) {
          const employeesData = await employeesResponse.json();
          setEmployees(employeesData);
        }
        
        // Fetch time entries summary
        const laborSummaryResponse = await fetch(`/api/time-entries/project/${id}/summary`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (laborSummaryResponse.ok) {
          const laborSummaryData = await laborSummaryResponse.json();
          setLaborSummary(laborSummaryData);
        }
        
        // Fetch change orders
        await fetchChangeOrders(id);
      } catch (err) {
        console.error('Error fetching project details:', err);
        toast.error('Failed to load project details');
        navigate('/projects');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchProject();
  }, [id, token, navigate]);
  
  // Format currency input (for editing forms)
  const formatCurrencyInput = (value) => {
    // Remove any non-digit characters
    const numericValue = value.replace(/\D/g, '');
    
    // Format with commas
    if (numericValue === '') return '';
    
    // Convert to number and format with commas
    const formattedValue = new Intl.NumberFormat('en-US', {
      style: 'decimal',
      maximumFractionDigits: 0
    }).format(parseInt(numericValue, 10));
    
    return formattedValue;
  };
  
  // Handle edit form change
  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    
    // Handle currency formatting for the new fields
    if (name === 'sumOfTimeAndMaterials' || name === 'generalRequirement') {
      const formattedValue = formatCurrencyInput(value);
      setEditFormData({
        ...editFormData,
        [name]: formattedValue
      });
    } else {
      setEditFormData({
        ...editFormData,
        [name]: value
      });
    }
  };
  
  // Handle project update
  const handleUpdateProject = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`/api/projects/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          projectName: editFormData.projectName,
          status: editFormData.status,
          startDate: editFormData.startDate || null,
          endDate: editFormData.endDate || null,
          sumOfTimeAndMaterials: editFormData.sumOfTimeAndMaterials || null,
          generalRequirement: editFormData.generalRequirement || null
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update project');
      }
      
      const updatedProject = await response.json();
      
      // Update project state
      setProject({
        ...project,
        ProjectName: updatedProject.ProjectName,
        Status: updatedProject.Status,
        StartDate: updatedProject.StartDate,
        EndDate: updatedProject.EndDate,
        SumOfTimeAndMaterials: updatedProject.SumOfTimeAndMaterials,
        GeneralRequirement: updatedProject.GeneralRequirement,
        TotalCost: updatedProject.TotalCost
      });
      
      setIsEditing(false);
      toast.success('Project updated successfully');
    } catch (err) {
      console.error('Error updating project:', err);
      toast.error('Failed to update project');
    }
  };
  
  // ============================================================================
  // COST ITEM HANDLERS
  // ============================================================================
  
  const handleCostItemFormChange = (e) => {
    const { name, value } = e.target;
    if (name === 'amount') {
      setCostItemForm({ ...costItemForm, [name]: formatCurrencyInput(value) });
    } else {
      setCostItemForm({ ...costItemForm, [name]: value });
    }
  };
  
  const handleAddCostItem = async () => {
    if (!costItemForm.description.trim() || !costItemForm.amount.trim()) {
      toast.error('Please enter both a description and amount');
      return;
    }
    
    try {
      const response = await fetch(`/api/projects/${id}/cost-items`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          description: costItemForm.description,
          amount: costItemForm.amount
        })
      });
      
      if (!response.ok) throw new Error('Failed to add cost item');
      
      const data = await response.json();
      setCostItems([...costItems, data.costItem]);
      
      // Update project totals
      setProject({
        ...project,
        SumOfTimeAndMaterials: data.sumOfTimeAndMaterials,
        TotalCost: data.totalCost
      });
      
      // Reset form
      setCostItemForm({ description: '', amount: '' });
      setShowAddCostItem(false);
      toast.success('Cost item added');
    } catch (err) {
      console.error('Error adding cost item:', err);
      toast.error('Failed to add cost item');
    }
  };
  
  const handleUpdateCostItem = async () => {
    if (!costItemForm.description.trim() || !costItemForm.amount.trim()) {
      toast.error('Please enter both a description and amount');
      return;
    }
    
    try {
      const response = await fetch(`/api/projects/${id}/cost-items/${editingCostItemId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          description: costItemForm.description,
          amount: costItemForm.amount
        })
      });
      
      if (!response.ok) throw new Error('Failed to update cost item');
      
      const data = await response.json();
      setCostItems(costItems.map(item =>
        item.CostItemId === editingCostItemId ? data.costItem : item
      ));
      
      // Update project totals
      setProject({
        ...project,
        SumOfTimeAndMaterials: data.sumOfTimeAndMaterials,
        TotalCost: data.totalCost
      });
      
      // Reset form
      setCostItemForm({ description: '', amount: '' });
      setEditingCostItemId(null);
      toast.success('Cost item updated');
    } catch (err) {
      console.error('Error updating cost item:', err);
      toast.error('Failed to update cost item');
    }
  };
  
  const handleDeleteCostItem = async (costItemId) => {
    if (!window.confirm('Are you sure you want to delete this cost item?')) return;
    
    try {
      const response = await fetch(`/api/projects/${id}/cost-items/${costItemId}`, {
        method: 'DELETE',
        headers: { 'x-auth-token': token }
      });
      
      if (!response.ok) throw new Error('Failed to delete cost item');
      
      const data = await response.json();
      setCostItems(costItems.filter(item => item.CostItemId !== costItemId));
      
      // Update project totals
      setProject({
        ...project,
        SumOfTimeAndMaterials: data.sumOfTimeAndMaterials,
        TotalCost: data.totalCost
      });
      
      toast.success('Cost item removed');
    } catch (err) {
      console.error('Error deleting cost item:', err);
      toast.error('Failed to delete cost item');
    }
  };
  
  const startEditCostItem = (item) => {
    setCostItemForm({
      description: item.Description,
      amount: parseFloat(item.Amount).toLocaleString('en-US', { maximumFractionDigits: 0 })
    });
    setEditingCostItemId(item.CostItemId);
    setShowAddCostItem(false);
  };
  
  const cancelCostItemEdit = () => {
    setCostItemForm({ description: '', amount: '' });
    setEditingCostItemId(null);
    setShowAddCostItem(false);
  };
  
  // Calculate cost items total for display
  const costItemsTotal = costItems.reduce((sum, item) => sum + parseFloat(item.Amount || 0), 0);
  
  // Handle scope form change
  const handleScopeFormChange = (e) => {
    const { name, value } = e.target;
    setScopeFormData({
      ...scopeFormData,
      [name]: value
    });
  };
  
  // Handle add scope
  const handleAddScope = async (formData) => {
    try {
      const response = await fetch(`/api/projects/${id}/scopes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          scopeName: formData.scopeName,
          scopeInformation: formData.scopeInformation
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to add scope');
      }
      
      const newScope = await response.json();
      
      // Update project state
      setProject({
        ...project,
        scopes: [...project.scopes, newScope]
      });
      
      // Reset form and close modal
      setScopeFormData({
        scopeName: '',
        scopeInformation: '• '
      });
      setShowScopeModal(false);
      toast.success('Scope added successfully');
    } catch (err) {
      console.error('Error adding scope:', err);
      toast.error('Failed to add scope');
    }
  };
  
  // Open edit scope modal
  const openEditScopeModal = (scope) => {
    setScopeFormData({
      scopeName: scope.ScopeName,
      scopeInformation: scope.ScopeInformation
    });
    setSelectedScopeId(scope.ScopeId);
    setIsEditingScope(true);
    setShowScopeModal(true);
  };
  
  // Handle update scope
  const handleUpdateScope = async (formData) => {
    try {
      const response = await fetch(`/api/projects/${id}/scopes/${selectedScopeId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          scopeName: formData.scopeName,
          scopeInformation: formData.scopeInformation
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update scope');
      }
      
      const updatedScope = await response.json();
      
      // Update project state
      setProject({
        ...project,
        scopes: project.scopes.map(scope => 
          scope.ScopeId === selectedScopeId ? updatedScope : scope
        )
      });
      
      // Reset form and close modal
      setScopeFormData({
        scopeName: '',
        scopeInformation: '• '
      });
      setShowScopeModal(false);
      setIsEditingScope(false);
      setSelectedScopeId(null);
      toast.success('Scope updated successfully');
    } catch (err) {
      console.error('Error updating scope:', err);
      toast.error('Failed to update scope');
    }
  };
  
  // Handle delete scope
  const handleDeleteScope = async (scopeId) => {
    if (!window.confirm('Are you sure you want to delete this scope?')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/projects/${id}/scopes/${scopeId}`, {
        method: 'DELETE',
        headers: {
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete scope');
      }
      
      // Update project state
      setProject({
        ...project,
        scopes: project.scopes.filter(scope => scope.ScopeId !== scopeId)
      });
      
      toast.success('Scope deleted successfully');
    } catch (err) {
      console.error('Error deleting scope:', err);
      toast.error('Failed to delete scope');
    }
  };
  
  // Handle file selection
  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };
  
  // Handle file upload
  const handleFileUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }
    
    setUploadingFile(true);
    
    const formData = new FormData();
    formData.append('document', selectedFile);
    formData.append('documentName', selectedFile.name);
    formData.append('documentType', selectedFile.type);
    
    try {
      const response = await fetch(`/api/documents/upload/${id}`, {
        method: 'POST',
        headers: {
          'x-auth-token': token
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error('Failed to upload document');
      }
      
      const newDocument = await response.json();
      
      // Update project state
      setProject({
        ...project,
        documents: [newDocument, ...project.documents]
      });
      
      // Reset form
      setSelectedFile(null);
      document.getElementById('document-upload').value = '';
      
      toast.success('Document uploaded successfully');
    } catch (err) {
      console.error('Error uploading document:', err);
      toast.error('Failed to upload document');
    } finally {
      setUploadingFile(false);
    }
  };
  
  // Handle generate combined proposal
  const handleGenerateCombinedProposal = async () => {
    if (!project.scopes || project.scopes.length === 0) {
      toast.error('Please add at least one scope before generating a proposal');
      return;
    }
    
    setIsGeneratingCombinedProposal(true);
    
    try {
      const response = await fetch(`/api/combined-proposal/${id}`, {
        method: 'POST',
        headers: {
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate combined proposal');
      }
      
      const proposalData = await response.json();
      
      // Update project documents
      const updatedProject = { ...project };
      if (!updatedProject.documents) {
        updatedProject.documents = [];
      }
      updatedProject.documents.unshift({
        DocumentId: proposalData.documentId,
        DocumentName: proposalData.documentName,
        DocumentPath: proposalData.documentPath,
        DocumentType: proposalData.documentType,
        CreatedAt: new Date().toISOString()
      });
      
      setProject(updatedProject);
      
      // Switch to the documents tab
      setActiveTab('documents');
      
      // Create the signing URL
      const signingUrl = `${window.location.origin}/sign/${proposalData.signingToken}`;
      
      // Show email preview modal instead of auto-sending
      setEmailPreviewType('proposal');
      setEmailDocumentId(id); // projectId for proposals
      setEmailSigningUrl(signingUrl);
      setEmailPreviewData({
        clientName: proposalData.clientName || project.ClientName,
        clientEmail: proposalData.clientEmail || project.ClientEmail,
        projectName: proposalData.projectName || project.ProjectName
      });
      setShowEmailPreviewModal(true);
      
    } catch (err) {
      console.error('Error generating combined proposal:', err);
      toast.error('Failed to generate combined proposal');
    } finally {
      setIsGeneratingCombinedProposal(false);
    }
  };
  
  // Handle generate proposal
  const handleGenerateProposal = async () => {
    if (!project.scopes || project.scopes.length === 0) {
      toast.error('Please add at least one scope before generating a proposal');
      return;
    }
    
    setIsGeneratingProposal(true);
    
    try {
      const response = await fetch(`/api/projects/${id}/generate-proposal`, {
        method: 'POST',
        headers: {
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate proposal');
      }
      
      const proposalData = await response.json();
      
      // Update project documents
      const updatedProject = { ...project };
      if (!updatedProject.documents) {
        updatedProject.documents = [];
      }
      updatedProject.documents.unshift({
        DocumentId: proposalData.documentId,
        DocumentName: proposalData.documentName,
        DocumentPath: proposalData.documentPath,
        DocumentType: proposalData.documentType,
        CreatedAt: new Date().toISOString()
      });
      
      setProject(updatedProject);
      
      // Switch to the documents tab after generating the proposal
      setActiveTab('documents');
      
      toast.success('Proposal generated successfully. You can download it from the Documents tab.');
    } catch (err) {
      console.error('Error generating proposal:', err);
      toast.error('Failed to generate proposal');
    } finally {
      setIsGeneratingProposal(false);
    }
  };
  
  // Handle delete document
  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }
    
    try {
      const response = await fetch(`/api/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'x-auth-token': token
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete document');
      }
      
      // Update project state
      setProject({
        ...project,
        documents: project.documents.filter(doc => doc.DocumentId !== documentId)
      });
      
      toast.success('Document deleted successfully');
    } catch (err) {
      console.error('Error deleting document:', err);
      toast.error('Failed to delete document');
    }
  };
  
  // Handle send email
  const handleSendEmail = async (formData) => {
    try {
      const response = await fetch(`/api/email/send-document/${selectedDocument.DocumentId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          subject: formData.subject,
          message: formData.message
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.msg || 'Failed to send email');
      }
      
      const data = await response.json();
      toast.success(data.msg || 'Email sent successfully');
    } catch (err) {
      console.error('Error sending email:', err);
      toast.error(err.message || 'Failed to send email');
      throw err;
    }
  };
  
  // Open email modal
  const openEmailModal = (document) => {
    setSelectedDocument(document);
    setShowEmailModal(true);
  };
  
  // Format currency
  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${parseFloat(value).toFixed(2)}`;
  };
  
  if (isLoading) {
    return <div className="text-center mt-5">Loading project details...</div>;
  }
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <h2 className="mb-0 me-3">{project.ProjectName}</h2>
          <Button 
            variant="outline-secondary" 
            size="sm"
            onClick={() => setIsEditing(true)}
          >
            Edit Project
          </Button>
        </div>
      </div>
      
      <Row>
        {/* Main Content Area */}
        <Col lg={9} md={8}>
          <Tab.Container id="project-tabs" activeKey={activeTab} onSelect={setActiveTab}>
        <Card className="mb-4">
          <Card.Header>
            <Nav variant="tabs">
              <Nav.Item>
                <Nav.Link eventKey="details">Details</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="scopes">Scopes</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="employees">Employees</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="labor">Labor</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="materials">Materials</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="changeOrders">Change Orders</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="documents">Documents</Nav.Link>
              </Nav.Item>
            </Nav>
          </Card.Header>
          <Card.Body>
            <Tab.Content>
              <Tab.Pane eventKey="details">
                {isEditing ? (
                  // Edit form
                  <Form onSubmit={handleUpdateProject}>
                    <Form.Group className="mb-3">
                      <Form.Label>Project Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="projectName"
                        value={editFormData.projectName}
                        onChange={handleEditFormChange}
                        required
                      />
                    </Form.Group>
                    
                    <Row>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Status</Form.Label>
                          <Form.Select
                            name="status"
                            value={editFormData.status}
                            onChange={handleEditFormChange}
                            required
                          >
                            <option value="New">New</option>
                            <option value="Proposal Sent">Proposal Sent</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Invoice Sent">Invoice Sent</option>
                            <option value="Completed">Completed</option>
                            <option value="On Hold">On Hold</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>Start Date</Form.Label>
                          <Form.Control
                            type="date"
                            name="startDate"
                            value={editFormData.startDate}
                            onChange={handleEditFormChange}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group className="mb-3">
                          <Form.Label>End Date</Form.Label>
                          <Form.Control
                            type="date"
                            name="endDate"
                            value={editFormData.endDate}
                            onChange={handleEditFormChange}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>General Requirement ($)</Form.Label>
                          <Form.Control
                            type="text"
                            name="generalRequirement"
                            value={editFormData.generalRequirement}
                            onChange={handleEditFormChange}
                            placeholder="Enter general requirement amount"
                          />
                          <Form.Text className="text-muted">
                            Cost items are managed in the Project Costs section below
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>
                    
                    <div className="d-flex justify-content-end">
                      <Button 
                        variant="secondary" 
                        className="me-2"
                        onClick={() => setIsEditing(false)}
                      >
                        Cancel
                      </Button>
                      <Button type="submit" variant="primary">
                        Save Changes
                      </Button>
                    </div>
                  </Form>
                ) : (
                  // Project details
                  <div>
                    <Row className="mb-3">
                      <Col md={6}>
                        <h5>Client Information</h5>
                        <p><strong>Name:</strong> {project.ClientName}</p>
                        <p><strong>Email:</strong> {project.ClientEmail || 'N/A'}</p>
                        <p><strong>Phone:</strong> {project.Phone || 'N/A'}</p>
                        <p><strong>Address:</strong> {project.Address || 'N/A'}</p>
                        <p>
                          {project.City && project.State && project.ZipCode
                            ? `${project.City}, ${project.State} ${project.ZipCode}`
                            : ''}
                        </p>
                      </Col>
                      <Col md={6}>
                        <h5>Project Status</h5>
                        <p>
                          <span 
                            style={{
                              backgroundColor: 
                                project.Status === 'New' ? '#007bff' :
                                project.Status === 'Proposal Sent' ? '#fd7e14' :
                                project.Status === 'Invoice Sent' ? '#8B5CF6' :
                                project.Status === 'In Progress' ? '#6c757d' :
                                project.Status === 'Completed' ? '#28a745' :
                                project.Status === 'On Hold' ? '#dc3545' :
                                '#6c757d',
                              color: '#fff',
                              padding: '0.375rem 0.75rem',
                              borderRadius: '0.375rem',
                              fontSize: '0.875rem',
                              fontWeight: '500',
                              display: 'inline-block',
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              border: 'none'
                            }}
                          >
                            {project.Status}
                          </span>
                        </p>
                        <p><strong>Created:</strong> {new Date(project.CreatedAt).toLocaleDateString()}</p>
                        <p>
                          <strong>Start Date:</strong> {
                            project.StartDate
                              ? new Date(project.StartDate).toLocaleDateString()
                              : 'Not set'
                          }
                        </p>
                        <p>
                          <strong>End Date:</strong> {
                            project.EndDate
                              ? new Date(project.EndDate).toLocaleDateString()
                              : 'Not set'
                          }
                        </p>
                      </Col>
                    </Row>
                    
                    {/* Project Costs Section */}
                    <Row className="mt-4">
                      <Col>
                        <Card>
                          <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">Project Costs</h5>
                            {!showAddCostItem && !editingCostItemId && (
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => {
                                  setCostItemForm({ description: '', amount: '' });
                                  setShowAddCostItem(true);
                                }}
                              >
                                + Add Cost Item
                              </Button>
                            )}
                          </Card.Header>
                          <Card.Body>
                            {costItems.length > 0 ? (
                              <Table hover size="sm">
                                <thead>
                                  <tr>
                                    <th>Description</th>
                                    <th style={{ width: '150px', textAlign: 'right' }}>Amount</th>
                                    <th style={{ width: '120px', textAlign: 'center' }}>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {costItems.map(item => (
                                    <tr key={item.CostItemId}>
                                      {editingCostItemId === item.CostItemId ? (
                                        <>
                                          <td>
                                            <Form.Control
                                              type="text"
                                              name="description"
                                              value={costItemForm.description}
                                              onChange={handleCostItemFormChange}
                                              placeholder="e.g., Cabinets"
                                              size="sm"
                                            />
                                          </td>
                                          <td>
                                            <Form.Control
                                              type="text"
                                              name="amount"
                                              value={costItemForm.amount}
                                              onChange={handleCostItemFormChange}
                                              placeholder="0"
                                              size="sm"
                                              style={{ textAlign: 'right' }}
                                            />
                                          </td>
                                          <td style={{ textAlign: 'center' }}>
                                            <Button
                                              variant="success"
                                              size="sm"
                                              className="me-1"
                                              onClick={handleUpdateCostItem}
                                            >
                                              Save
                                            </Button>
                                            <Button
                                              variant="secondary"
                                              size="sm"
                                              onClick={cancelCostItemEdit}
                                            >
                                              Cancel
                                            </Button>
                                          </td>
                                        </>
                                      ) : (
                                        <>
                                          <td>{item.Description}</td>
                                          <td style={{ textAlign: 'right' }}>
                                            ${parseFloat(item.Amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                          </td>
                                          <td style={{ textAlign: 'center' }}>
                                            <Button
                                              variant="outline-primary"
                                              size="sm"
                                              className="me-1"
                                              onClick={() => startEditCostItem(item)}
                                            >
                                              Edit
                                            </Button>
                                            <Button
                                              variant="outline-danger"
                                              size="sm"
                                              onClick={() => handleDeleteCostItem(item.CostItemId)}
                                            >
                                              Delete
                                            </Button>
                                          </td>
                                        </>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            ) : !showAddCostItem ? (
                              <p className="text-muted text-center mb-0">
                                No cost items added yet. Click "Add Cost Item" to itemize project costs.
                              </p>
                            ) : null}
                            
                            {/* Add new cost item row */}
                            {showAddCostItem && (
                              <div className="border rounded p-3 mb-3">
                                <Row>
                                  <Col md={6}>
                                    <Form.Group className="mb-2">
                                      <Form.Label className="small">Description</Form.Label>
                                      <Form.Control
                                        type="text"
                                        name="description"
                                        value={costItemForm.description}
                                        onChange={handleCostItemFormChange}
                                        placeholder="e.g., Cabinets, Trim, Interior Paint"
                                        size="sm"
                                        autoFocus
                                      />
                                    </Form.Group>
                                  </Col>
                                  <Col md={3}>
                                    <Form.Group className="mb-2">
                                      <Form.Label className="small">Amount ($)</Form.Label>
                                      <Form.Control
                                        type="text"
                                        name="amount"
                                        value={costItemForm.amount}
                                        onChange={handleCostItemFormChange}
                                        placeholder="0"
                                        size="sm"
                                      />
                                    </Form.Group>
                                  </Col>
                                  <Col md={3} className="d-flex align-items-end mb-2">
                                    <Button
                                      variant="success"
                                      size="sm"
                                      className="me-2"
                                      onClick={handleAddCostItem}
                                    >
                                      Add
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={cancelCostItemEdit}
                                    >
                                      Cancel
                                    </Button>
                                  </Col>
                                </Row>
                              </div>
                            )}
                            
                            {/* Totals */}
                            <div className="border-top pt-3 mt-2">
                              <Row>
                                <Col md={8}>
                                  <strong>Sum of Time and Materials:</strong>
                                </Col>
                                <Col md={4} className="text-end">
                                  <strong>
                                    ${costItemsTotal > 0
                                      ? costItemsTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })
                                      : project.SumOfTimeAndMaterials
                                        ? parseFloat(project.SumOfTimeAndMaterials).toLocaleString('en-US', { minimumFractionDigits: 2 })
                                        : '0.00'
                                    }
                                  </strong>
                                </Col>
                              </Row>
                              {project.GeneralRequirement && parseFloat(project.GeneralRequirement) > 0 && (
                                <Row>
                                  <Col md={8}>
                                    <strong>General Requirement:</strong>
                                  </Col>
                                  <Col md={4} className="text-end">
                                    <strong>
                                      ${parseFloat(project.GeneralRequirement).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                                    </strong>
                                  </Col>
                                </Row>
                              )}
                              <Row className="mt-2 pt-2 border-top">
                                <Col md={8}>
                                  <h5 className="mb-0 text-primary">Total Project Cost:</h5>
                                </Col>
                                <Col md={4} className="text-end">
                                  <h5 className="mb-0 text-primary">
                                    ${
                                      (() => {
                                        const itemsSum = costItemsTotal > 0
                                          ? costItemsTotal
                                          : parseFloat(project.SumOfTimeAndMaterials || 0);
                                        const genReq = parseFloat(project.GeneralRequirement || 0);
                                        return (itemsSum + genReq).toLocaleString('en-US', { minimumFractionDigits: 2 });
                                      })()
                                    }
                                  </h5>
                                </Col>
                              </Row>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </div>
                )}
              </Tab.Pane>
              
              <Tab.Pane eventKey="scopes">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Project Scopes</h5>
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={() => {
                      console.log('Setting initial scope data with bullet point');
                      setScopeFormData({ scopeName: '', scopeInformation: '• ' });
                      setIsEditingScope(false);
                      setShowScopeModal(true);
                    }}
                  >
                    Add Scope
                  </Button>
                </div>
                
                {project.scopes && project.scopes.length > 0 ? (
                  project.scopes.map((scope, index) => (
                    <Card key={scope.ScopeId} className="scope-card mb-3">
                      <Card.Header className="scope-header d-flex justify-content-between align-items-center">
                        <h5 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: '600' }}>{scope.ScopeName}</h5>
                        <div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-2"
                            onClick={() => openEditScopeModal(scope)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDeleteScope(scope.ScopeId)}
                          >
                            Delete
                          </Button>
                        </div>
                      </Card.Header>
                      <Card.Body>
                        <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '1.05rem', lineHeight: '1.6' }}>
                          {scope.ScopeInformation}
                        </pre>
                      </Card.Body>
                    </Card>
                  ))
                ) : (
                  <p className="text-center">No scopes added yet. Add a scope to include in the project proposal.</p>
                )}
              </Tab.Pane>

              <Tab.Pane eventKey="employees">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Assigned Employees</h5>
                  <Button
                    variant="primary"
                    size="sm"
                    as={Link}
                    to={`/projects/${id}/assign-employees`}
                  >
                    <FaUserPlus className="me-1" />
                    Assign Employees
                  </Button>
                </div>
                
                {employees.length > 0 ? (
                  <div className="table-responsive">
                    <Table striped hover>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Contact</th>
                          <th>Hourly Rate</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {employees.map(employee => (
                          <tr key={employee.EmployeeId}>
                            <td>
                              <Link to={`/employees/${employee.EmployeeId}`}>
                                {employee.FirstName} {employee.LastName}
                              </Link>
                            </td>
                            <td>
                              <div>{employee.Email}</div>
                              <div>{employee.Phone}</div>
                            </td>
                            <td>{formatCurrency(employee.HourlyWage)}</td>
                            <td>
                              <Button
                                as={Link}
                                to={`/projects/${id}/time-entries?employeeId=${employee.EmployeeId}`}
                                variant="outline-primary"
                                size="sm"
                                className="me-1"
                              >
                                <FaClock className="me-1" />
                                Time Entries
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p>No employees assigned to this project.</p>
                    <Button
                      as={Link}
                      to={`/projects/${id}/assign-employees`}
                      variant="primary"
                    >
                      <FaUserPlus className="me-1" />
                      Assign Employees
                    </Button>
                  </div>
                )}
              </Tab.Pane>
              
              <Tab.Pane eventKey="labor">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Labor Cost & Time Tracking</h5>
                  <div>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      as={Link}
                      to={`/projects/${id}/time-entries`}
                      className="me-2"
                    >
                      <FaList className="me-1" />
                      View All Time Entries
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      as={Link}
                      to={`/projects/${id}/assign-employees`}
                    >
                      <FaUserPlus className="me-1" />
                      {employees.length > 0 ? 'Add More Employees' : 'Assign Employees'}
                    </Button>
                  </div>
                </div>
                
                {laborSummary ? (
                  <>
                    <Card className="mb-4">
                      <Card.Body>
                        <Row>
                          <Col md={4}>
                            <div className="mb-3">
                              <h6>Regular Hours</h6>
                              <h4>{laborSummary.projectTotal.TotalRegularHours?.toFixed(2) || '0.00'}</h4>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="mb-3">
                              <h6>Overtime Hours</h6>
                              <h4>{laborSummary.projectTotal.TotalOvertimeHours?.toFixed(2) || '0.00'}</h4>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div className="mb-3">
                              <h6>Total Hours</h6>
                              <h4>{laborSummary.projectTotal.TotalHours?.toFixed(2) || '0.00'}</h4>
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <Col md={4}>
                            <div>
                              <h6>Regular Labor Cost</h6>
                              <h4>{formatCurrency(laborSummary.projectTotal.RegularCost)}</h4>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div>
                              <h6>Overtime Labor Cost</h6>
                              <h4>{formatCurrency(laborSummary.projectTotal.OvertimeCost)}</h4>
                            </div>
                          </Col>
                          <Col md={4}>
                            <div>
                              <h6>Total Labor Cost</h6>
                              <h4 className="text-primary">{formatCurrency(laborSummary.projectTotal.TotalCost)}</h4>
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  
                    {laborSummary.employees.length > 0 ? (
                      <div className="table-responsive">
                        <Table striped hover>
                          <thead>
                            <tr>
                              <th>Employee</th>
                              <th>Regular Hours</th>
                              <th>Overtime Hours</th>
                              <th>Total Hours</th>
                              <th>Labor Cost</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {laborSummary.employees.map(employeeSummary => (
                              <tr key={employeeSummary.EmployeeId}>
                                <td>
                                  <Link to={`/employees/${employeeSummary.EmployeeId}`}>
                                    {employeeSummary.FirstName} {employeeSummary.LastName}
                                  </Link>
                                  <span 
                                    style={{
                                      backgroundColor: '#6c757d',
                                      color: '#fff',
                                      padding: '0.25rem 0.5rem',
                                      borderRadius: '0.375rem',
                                      fontSize: '0.75rem',
                                      fontWeight: '500',
                                      display: 'inline-block',
                                      marginLeft: '0.5rem'
                                    }}
                                  >
                                    {formatCurrency(employeeSummary.HourlyWage)}/hr
                                  </span>
                                </td>
                                <td>{parseFloat(employeeSummary.TotalRegularHours).toFixed(2)}</td>
                                <td>{parseFloat(employeeSummary.TotalOvertimeHours || 0).toFixed(2)}</td>
                                <td>
                                  {(
                                    parseFloat(employeeSummary.TotalRegularHours) + 
                                    parseFloat(employeeSummary.TotalOvertimeHours || 0)
                                  ).toFixed(2)}
                                </td>
                                <td>{formatCurrency(employeeSummary.TotalCost)}</td>
                                <td>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    as={Link}
                                    to={`/projects/${id}/time-entries?employeeId=${employeeSummary.EmployeeId}`}
                                  >
                                    View Entries
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    ) : (
                      <Alert variant="info">
                        No time entries recorded yet. Add time entries to track labor costs.
                      </Alert>
                    )}
                  </>
                ) : employees.length > 0 ? (
                  <Alert variant="info">
                    No time entries recorded yet. Start tracking labor hours by adding time entries.
                    <div className="mt-3">
                      <Button
                        as={Link}
                        to={`/projects/${id}/time-entries`}
                        variant="primary"
                      >
                        Add Time Entries
                      </Button>
                    </div>
                  </Alert>
                ) : (
                  <Alert variant="info">
                    You need to assign employees to this project before you can track labor hours.
                    <div className="mt-3">
                      <Button
                        as={Link}
                        to={`/projects/${id}/assign-employees`}
                        variant="primary"
                      >
                        Assign Employees
                      </Button>
                    </div>
                  </Alert>
                )}
              </Tab.Pane>
              
              <Tab.Pane eventKey="materials">
                <MaterialsTab projectId={id} token={token} />
              </Tab.Pane>
              
              <Tab.Pane eventKey="changeOrders">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">Change Orders</h5>
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={() => setShowChangeOrderModal(true)}
                  >
                    <FaExchangeAlt className="me-1" />
                    New Change Order
                  </Button>
                </div>
                <ChangeOrderList
                  changeOrders={changeOrders}
                  onRefresh={() => fetchChangeOrders(id)}
                  projectId={id}
                  token={token}
                  project={project}
                  openEmailModal={openEmailModal}
                />
              </Tab.Pane>
              
              <Tab.Pane eventKey="documents">
                <DocumentsTab 
                  documents={project.documents || []}
                  projectId={id}
                  token={token}
                  onDeleteDocument={handleDeleteDocument}
                  onUploadDocument={handleFileUpload}
                  onEmailDocument={openEmailModal}
                  clientEmail={project.ClientEmail}
                  clientName={project.ClientName}
                  projectName={project.ProjectName}
                  uploadingFile={uploadingFile}
                  selectedFile={selectedFile}
                  onFileChange={handleFileChange}
                />
              </Tab.Pane>
            </Tab.Content>
          </Card.Body>
        </Card>
      </Tab.Container>
        </Col>
        
        {/* Right Sidebar - Action Buttons */}
        <Col lg={3} md={4}>
          <Card className="sticky-top" style={{ top: '20px' }}>
            <Card.Header className="bg-light">
              <strong>Actions</strong>
            </Card.Header>
            <Card.Body className="d-grid gap-2">
              <Button
                variant="outline-primary"
                as={Link}
                to={`/projects/${id}/payment-schedule`}
              >
                <FaFileInvoiceDollar className="me-2" />
                Payment Schedule
              </Button>
              
              <Button
                variant="primary"
                onClick={handleGenerateCombinedProposal}
                disabled={isGeneratingCombinedProposal}
              >
                <FaFileAlt className="me-2" />
                {isGeneratingCombinedProposal ? 'Generating...' : 'Generate Proposal'}
              </Button>
              
              <Button
                variant="warning"
                onClick={() => setShowChangeOrderModal(true)}
              >
                <FaExchangeAlt className="me-2" />
                Change Order
              </Button>
              
              <Button
                variant="success"
                onClick={() => setShowInvoiceModal(true)}
              >
                <FaFileInvoiceDollar className="me-2" />
                Generate Invoice
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Scope Modal */}
      <ScopeModal
        show={showScopeModal}
        onHide={() => setShowScopeModal(false)}
        onSubmit={isEditingScope ? handleUpdateScope : handleAddScope}
        isEditing={isEditingScope}
        initialData={scopeFormData}
      />
      
      {/* Email Document Modal */}
      <EmailDocumentModal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        onSend={handleSendEmail}
        document={selectedDocument}
        clientName={project?.ClientName}
        clientEmail={project?.ClientEmail}
        companyName={user?.customer}
      />
      
      {/* Change Order Modal */}
      <ChangeOrderModal
        show={showChangeOrderModal}
        onHide={() => setShowChangeOrderModal(false)}
        project={{
          ProjectId: project?.ProjectId,
          ProjectName: project?.ProjectName,
          ClientId: project?.ClientId,
          ClientName: project?.ClientName,
          Address: project?.Address,
          City: project?.City,
          State: project?.State,
          ZipCode: project?.ZipCode,
          Phone: project?.Phone,
          Email: project?.ClientEmail
        }}
        onSuccess={async (data) => {
          // Re-fetch project to get updated documents list
          try {
            const response = await fetch(`/api/projects/${id}`, {
              headers: { 'x-auth-token': token }
            });
            if (response.ok) {
              const updatedProject = await response.json();
              setProject(updatedProject);
            }
          } catch (err) {
            console.error('Error refreshing project:', err);
          }
          // Switch to documents tab
          setActiveTab('documents');
          // Re-fetch change orders
          await fetchChangeOrders(id);
          
          // Show email preview modal for change order
          const signingUrl = data.signingUrl || `${window.location.origin}/sign/${data.signingToken}`;
          setEmailPreviewType('change_order');
          setEmailDocumentId(data.changeOrderId);
          setEmailSigningUrl(signingUrl);
          setEmailPreviewData({
            clientName: data.clientName || project?.ClientName,
            clientEmail: data.clientEmail || project?.ClientEmail,
            projectName: data.projectName || project?.ProjectName,
            changeOrderNumber: data.changeOrderNumber,
            description: data.description,
            amount: data.amount
          });
          setShowEmailPreviewModal(true);
        }}
      />
      
      {/* Invoice Modal */}
      <InvoiceModal
        show={showInvoiceModal}
        onHide={() => setShowInvoiceModal(false)}
        projectId={project?.ProjectId}
        onInvoiceCreated={async (data) => {
          // Re-fetch project to get updated documents list
          try {
            const response = await fetch(`/api/projects/${id}`, {
              headers: { 'x-auth-token': token }
            });
            if (response.ok) {
              const updatedProject = await response.json();
              setProject(updatedProject);
            }
          } catch (err) {
            console.error('Error refreshing project:', err);
          }
          // Switch to documents tab
          setActiveTab('documents');
          
          // Show email preview modal for invoice
          setEmailPreviewType('invoice');
          setEmailDocumentId(data.invoiceId);
          setEmailSigningUrl(null); // Invoices don't have signing URLs
          setEmailPreviewData({
            clientName: data.clientName || project?.ClientName,
            clientEmail: data.clientEmail || project?.ClientEmail,
            projectName: data.projectName || project?.ProjectName,
            invoiceNumber: data.invoiceNumber,
            totalDue: data.totalDue,
            dueDate: data.dueDate
          });
          setShowEmailPreviewModal(true);
        }}
      />
      
      {/* Email Preview Modal - For Proposals, Change Orders, Invoices */}
      <EmailPreviewModal
        show={showEmailPreviewModal}
        onHide={() => {
          setShowEmailPreviewModal(false);
          setEmailPreviewData(null);
          setEmailPreviewType(null);
          setEmailDocumentId(null);
          setEmailSigningUrl(null);
        }}
        type={emailPreviewType}
        documentId={emailDocumentId}
        emailData={emailPreviewData}
        signingUrl={emailSigningUrl}
        onEmailSent={() => {
          // Email was sent successfully
          toast.success('Document created and email sent!');
        }}
      />
    </div>
  );
};

export default ProjectDetail;